import { useState, useCallback } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Typography,
  Button,
  Checkbox,
  FormControlLabel,
  Autocomplete,
  Skeleton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  styled,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { usePartyManager } from './PartyManagerContext';
import {
  POSSIBLE_SOURCES,
  EDIT_SOURCE,
  CODEMETA_SOURCE,
} from './partyManagerUtils';
import countriesData from '@assets/countries.json';
import { useInterfaceStore } from '@/store/interface';
import proxy from '@/services/ProxyService';
import axios from 'axios';
import { debounce } from 'lodash';

const getButtonIcon = (selectedSource) => {
  if (selectedSource === 'edit') return EDIT_SOURCE.icon;
  if (selectedSource === 'codemeta') {
    return (
      <img
        src={CODEMETA_SOURCE.icon}
        alt={CODEMETA_SOURCE.name}
        style={{ width: '24px', height: '24px' }}
      />
    );
  }
  const sourceInfo = POSSIBLE_SOURCES[selectedSource];
  if (sourceInfo?.icon) {
    return (
      <img
        src={sourceInfo.icon}
        alt={sourceInfo.name}
        style={{ width: '24px', height: '24px' }}
      />
    );
  }
  return '?';
};

const AddressSkeleton = () => (
  <>
    <Skeleton variant="text" width="150px" height={30} />
    <Skeleton variant="rounded" width="100%" height={40} />
    <Skeleton variant="rounded" width="100%" height={40} />
    <Skeleton variant="rounded" width="100%" height={40} />
    <Skeleton variant="rounded" width="100%" height={40} />
    <Skeleton variant="rounded" width="100%" height={40} />
  </>
);

const StrongBorderAccordion = styled(Accordion)(({ theme }) => ({
  border: `1px solid ${theme.palette.grey[300]}`,
  '&:not(:last-of-type)': {
    borderBottom: `0px solid ${theme.palette.grey[300]}`,
  },
  '&:before': {
    display: 'none',
  },
}));

const AddressField = ({ label, fieldKey, addressIndex }) => {
  const {
    partyData,
    handleAddressFieldChange,
    handleAddressCycleSource,
    getAvailableAddressSources,
  } = usePartyManager();

  const fieldState = partyData.addresses[addressIndex]?.[fieldKey];
  if (!fieldState) return null;

  const availableSources = getAvailableAddressSources(addressIndex, fieldKey);
  const displayValue =
    fieldState.selected === 'edit'
      ? fieldState.edit
      : fieldState[fieldState.selected];

  return (
    <Box
      sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%' }}
    >
      <TextField
        label={label}
        size="small"
        value={displayValue || ''}
        onChange={(e) =>
          handleAddressFieldChange(addressIndex, fieldKey, e.target.value)
        }
        sx={{
          flex: 1,
          '& .MuiInputBase-input': {
            color: 'rgba(0, 0, 0, 0.87)',
          },
          '& .MuiInputLabel-root': {
            color: 'rgba(0, 0, 0, 0.38)',
            '&.MuiInputLabel-shrink': {
              color: 'rgba(0, 0, 0, 0.87)',
            },
          },
        }}
        autoComplete="nope"
      />
      {availableSources.length > 1 && (
        <Button
          title={`Quelle wechseln: Aktuell ${fieldState.selected}. Verfügbar: ${availableSources.join(', ')}`}
          sx={{
            width: '40px',
            height: '40px',
            minWidth: '40px',
            maxWidth: '40px',
            flexShrink: 0,
          }}
          onClick={() => handleAddressCycleSource(addressIndex, fieldKey)}
          variant="outlined"
        >
          {getButtonIcon(fieldState.selected)}
        </Button>
      )}
    </Box>
  );
};

const AddressAutocomplete = ({ label, fieldKey, addressIndex }) => {
  const INTERFACE_STORE = useInterfaceStore.getState();
  const [results, setResults] = useState([]);
  const {
    partyData,
    handleAddressFieldChange,
    handleAddressCycleSource,
    getAvailableAddressSources,
  } = usePartyManager();

  const handleSearch = async (query) => {
    if (query.length > 3 && INTERFACE_STORE.openRoute) {
      const res = await proxy.get(
        `${INTERFACE_STORE.openRoute}/geocode/autocomplete?text=${encodeURIComponent(query)}&focus.point.lon=6.536722&focus.point.lat=49.948018`,
      );
      const data =
        res.data?.features?.filter((feature) => feature.properties?.street) ||
        [];
      setResults(data);
      return;
    }
    setResults([]);
  };

  const debouncedHandleSearch = useCallback(debounce(handleSearch, 1000), []);

  const fieldState = partyData.addresses[addressIndex]?.[fieldKey];
  if (!fieldState) {
    return null;
  }
  const availableSources = getAvailableAddressSources(addressIndex, fieldKey);
  const displayValue =
    fieldState.selected === 'edit'
      ? fieldState.edit
      : fieldState[fieldState.selected];

  return (
    <Box
      sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%' }}
    >
      <Autocomplete
        freeSolo
        options={results}
        sx={{ flex: 1 }}
        filterOptions={() => results}
        getOptionLabel={(option) => option.properties?.label || ''}
        onChange={async (_, newValue) => {
          const { data } = await axios.get(
            `https://openplzapi.org/de/FullTextSearch?searchTerm=${encodeURIComponent(`${newValue?.properties?.locality || ''}, ${newValue?.properties?.street?.replace(/(Strasse|Straße)/i, 'str') || ''}`)}`,
          );
          const zipcode = data?.[0]?.postalCode || '';
          handleAddressFieldChange(
            addressIndex,
            fieldKey,
            newValue?.properties?.street || '',
          );
          handleAddressFieldChange(addressIndex, 'zipcode', zipcode);
          handleAddressFieldChange(
            addressIndex,
            'city',
            newValue?.properties?.locality || '',
          );
          handleAddressFieldChange(
            addressIndex,
            'countryCode',
            newValue?.properties?.country_a?.substr(0, 2) || '',
          );
          setResults([]); // Clear results after selection
        }}
        onInputChange={(e, value) => {
          handleAddressFieldChange(addressIndex, fieldKey, value);
          debouncedHandleSearch(value);
        }}
        inputValue={displayValue || ''}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            size="small"
            sx={{
              flex: 1,
              '& .MuiInputBase-input': {
                color: 'rgba(0, 0, 0, 0.87)',
              },
              '& .MuiInputLabel-root': {
                color: 'rgba(0, 0, 0, 0.38)',
                '&.MuiInputLabel-shrink': {
                  color: 'rgba(0, 0, 0, 0.87)',
                },
              },
            }}
            slotProps={{
              htmlInput: {
                ...params?.slotProps?.htmlInput,
                ...params?.inputProps,
                autoComplete: 'nope',
              },
            }}
          />
        )}
      />
      {availableSources.length > 1 && (
        <Button
          title={`Quelle wechseln: Aktuell ${fieldState.selected}. Verfügbar: ${availableSources.join(', ')}`}
          sx={{
            width: '40px',
            height: '40px',
            minWidth: '40px',
            maxWidth: '40px',
            flexShrink: 0,
          }}
          onClick={() => handleAddressCycleSource(addressIndex, fieldKey)}
          variant="outlined"
        >
          {getButtonIcon(fieldState.selected)}
        </Button>
      )}
    </Box>
  );
};

const AddressCountryField = ({ addressIndex, disabled }) => {
  const {
    partyData,
    handleAddressFieldChange,
    handleAddressCycleSource,
    getAvailableAddressSources,
  } = usePartyManager();
  const [countries] = useState(countriesData);

  const fieldKey = 'countryCode';
  const fieldState = partyData.addresses[addressIndex]?.[fieldKey];
  if (!fieldState) return null;

  const availableSources = getAvailableAddressSources(addressIndex, fieldKey);
  const displayValue =
    fieldState.selected === 'edit'
      ? fieldState.edit
      : fieldState[fieldState.selected];
  const selectedCountry =
    countries.find((c) => c.code === displayValue) || null;

  return (
    <Box
      sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%' }}
    >
      <Autocomplete
        id={`country-select-${addressIndex}`}
        options={countries}
        size="small"
        sx={{
          flex: 1,
          '& .MuiInputBase-input': {
            color: 'rgba(0, 0, 0, 0.87)',
          },
          '& .MuiInputLabel-root': {
            color: 'rgba(0, 0, 0, 0.38)',
            '&.MuiInputLabel-shrink': {
              color: 'rgba(0, 0, 0, 0.87)',
            },
          },
        }}
        getOptionLabel={(option) => option.label}
        disabled={disabled}
        value={selectedCountry}
        onChange={(_, newValue) => {
          handleAddressFieldChange(
            addressIndex,
            fieldKey,
            newValue?.code || '',
          );
        }}
        renderOption={(props, option) => {
          const { key, ...optionProps } = props;
          return (
            <Box
              key={key}
              component="li"
              sx={{ '& > img': { mr: 2, flexShrink: 0 } }}
              {...optionProps}
            >
              <img
                loading="lazy"
                width="20"
                srcSet={`https://flagcdn.com/w40/${option.code.toLowerCase()}.png 2x`}
                src={`https://flagcdn.com/w20/${option.code.toLowerCase()}.png`}
                alt=""
              />
              {option.label} ({option.code})
            </Box>
          );
        }}
        renderInput={(params) => (
          <TextField {...params} label={<Typography>Land</Typography>} />
        )}
      />
      {availableSources.length > 1 && (
        <Button
          title={`Quelle wechseln: Aktuell ${fieldState.selected}.`}
          sx={{
            width: '40px',
            height: '40px',
            minWidth: '40px',
            maxWidth: '40px',
          }}
          onClick={() => handleAddressCycleSource(addressIndex, fieldKey)}
          variant="outlined"
        >
          {getButtonIcon(fieldState.selected)}
        </Button>
      )}
    </Box>
  );
};

export default function PartyAddresses({ disabled }) {
  const {
    partyData,
    isPartyManagerLoading,
    handleRemoveAddress,
    handleAddressPrimaryChange,
  } = usePartyManager();

  const [expandedAccordions, setExpandedAccordions] = useState([0]);

  const handleAccordionChange = (index) => (event, isExpanded) => {
    setExpandedAccordions((currentExpanded) =>
      isExpanded
        ? [...currentExpanded, index]
        : currentExpanded.filter((i) => i !== index),
    );
  };

  const getDisplayValue = (fieldState) => {
    if (!fieldState) return '';
    return fieldState.selected === 'edit'
      ? fieldState.edit
      : fieldState[fieldState.selected] || '';
  };

  if (isPartyManagerLoading) {
    return (
      <StrongBorderAccordion expanded disabled>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Skeleton variant="text" width="60%" height={24} />
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexFlow: 'column', gap: 1.5 }}>
            <AddressSkeleton />
          </Box>
        </AccordionDetails>
      </StrongBorderAccordion>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
      }}
    >
      {partyData.addresses &&
        partyData.addresses.map((address, index) => {
          const street = getDisplayValue(address.street1);
          const city = getDisplayValue(address.city);
          const summaryText =
            [street, city].filter(Boolean).join(', ') || 'Neue Adresse';

          return (
            <StrongBorderAccordion
              key={index}
              expanded={expandedAccordions.includes(index)}
              onChange={handleAccordionChange(index)}
              disabled={disabled}
            >
              <AccordionSummary
                component="div"
                expandIcon={<ExpandMoreIcon />}
                aria-controls={`panel${index}bh-content`}
                id={`panel${index}bh-header`}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    gap: 1,
                  }}
                >
                  <FormControlLabel
                    onClick={(event) => event.stopPropagation()}
                    control={
                      <Checkbox
                        checked={!!address.primeAddress}
                        onChange={() => handleAddressPrimaryChange(index)}
                        disabled={disabled}
                      />
                    }
                    label="Primär"
                  />
                  <Typography sx={{ flexShrink: 0, mr: 2 }}>
                    {summaryText}
                  </Typography>
                  <Box sx={{ flexGrow: 1 }} />
                  <IconButton
                    onClick={(event) => {
                      event.stopPropagation();
                      handleRemoveAddress(index);
                    }}
                    onFocus={(event) => event.stopPropagation()}
                    aria-label="Adresse löschen"
                    size="small"
                    disabled={disabled}
                    sx={{
                      color: 'error.dark',
                    }}
                  >
                    <DeleteIcon fontSize="inherit" />
                  </IconButton>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box
                  sx={{
                    display: 'flex',
                    flexFlow: 'column',
                    gap: 1.5,
                  }}
                >
                  <AddressAutocomplete
                    addressIndex={index}
                    fieldKey="street1"
                    label="Straße und Nr."
                  />
                  <AddressField
                    addressIndex={index}
                    fieldKey="street2"
                    label="Adresszusatz"
                  />
                  <AddressField
                    addressIndex={index}
                    fieldKey="zipcode"
                    label="Postleitzahl"
                  />
                  <AddressField
                    addressIndex={index}
                    fieldKey="city"
                    label="Stadt"
                  />
                  <AddressCountryField
                    addressIndex={index}
                    disabled={disabled}
                  />
                </Box>
              </AccordionDetails>
            </StrongBorderAccordion>
          );
        })}
    </Box>
  );
}
